// filepath: client/src/components/common/ImagePreloader.tsx
import React, { useEffect } from 'react';

interface ImagePreloaderProps {
  images: string[];
  priority?: boolean;
}

const ImagePreloader: React.FC<ImagePreloaderProps> = ({ images, priority = false }) => {
  useEffect(() => {
    const preloadImages = () => {
      images.forEach((src) => {
        const img = new Image();
        img.src = src;

        // Add to browser cache
        if (priority) {
          // Check if preload link already exists
          const existingLink = document.querySelector(`link[href="${src}"]`);
          if (!existingLink) {
            // For critical images, add them to the document head
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
          }
        }
      });
    };

    // Preload images immediately for critical images, with delay for others
    const timer = setTimeout(preloadImages, priority ? 0 : 100);

    return () => clearTimeout(timer);
  }, [images, priority]);

  return null; // This component doesn't render anything
};

// Hook for preloading images
export const useImagePreloader = (images: string[], priority = false) => {
  useEffect(() => {
    const preloadImages = () => {
      images.forEach((src) => {
        const img = new Image();
        img.src = src;

        if (priority) {
          // Check if preload link already exists
          const existingLink = document.querySelector(`link[href="${src}"]`);
          if (!existingLink) {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'image';
            link.href = src;
            link.crossOrigin = 'anonymous';
            document.head.appendChild(link);
          }
        }
      });
    };

    const timer = setTimeout(preloadImages, priority ? 0 : 100);
    return () => clearTimeout(timer);
  }, [images, priority]);
};

// Background images used in the application
export const BACKGROUND_IMAGES = {
  hero: '/assets/images/hero-background.jpg',
  login: '/assets/images/login-background.jpg',
  documentation: '/assets/images/documentation-hero.jpg'
};

// Critical images that should be preloaded with high priority
// Only preload images that are likely to be used immediately
export const CRITICAL_IMAGES = [
  // Only preload hero image for landing page
  BACKGROUND_IMAGES.hero
];

// All background images for general preloading
export const ALL_BACKGROUND_IMAGES = Object.values(BACKGROUND_IMAGES);

export default ImagePreloader;
